import React from "react";
import { FiArrowUpRight, FiArrowRight } from "react-icons/fi";
import { BsPeople, BsShieldCheck } from "react-icons/bs";
import {
  FaRocket,
  FaCode,
  FaGlobe,
  FaReact,
  FaNodeJs,
  FaPython,
  FaAws,
  FaDocker,
  FaJava,
} from "react-icons/fa";
import {
  SiTypescript,
  SiJavascript,
  SiPostgresql,
  SiRedis,
} from "react-icons/si";
import {
  HiOutlineLightBulb,
  HiOutlineShieldCheck,
  HiOutlineGlobe,
  HiOutlineChip,
} from "react-icons/hi";
import { pageTitle } from "../../helpers/PageTitle";

const values = [
  {
    icon: <HiOutlineLightBulb className="h-6 w-6" />,
    title: "Innovation First",
    desc: "We push boundaries with cutting-edge tech to solve tomorrow's problems today.",
  },
  {
    icon: <HiOutlineShieldCheck className="h-6 w-6" />,
    title: "Code Integrity",
    desc: "Clean, scalable, and secure code that stands the test of time and scale.",
  },
  {
    icon: <HiOutlineGlobe className="h-6 w-6" />,
    title: "Global Delivery",
    desc: "Serving clients worldwide with reliable, enterprise-grade delivery.",
  },
  {
    icon: <HiOutlineChip className="h-6 w-6" />,
    title: "AI-Powered",
    desc: "Integrating machine learning and intelligent automation into every solution.",
  },
];

const techStack = [
  { name: "React", icon: FaReact, color: "text-[#61DAFB]" },
  { name: "Node.js", icon: FaNodeJs, color: "text-[#339933]" },
  { name: "Python", icon: FaPython, color: "text-[#3776AB]" },
  { name: "TypeScript", icon: SiTypescript, color: "text-[#3178C6]" },
  { name: "JavaScript", icon: SiJavascript, color: "text-[#F7DF1E]" },
  { name: "Java", icon: FaJava, color: "text-[#E76F00]" },
  { name: "AWS", icon: FaAws, color: "text-[#FF9900]" },
  { name: "Docker", icon: FaDocker, color: "text-[#2496ED]" },
  { name: "PostgreSQL", icon: SiPostgresql, color: "text-[#4169E1]" },
  { name: "Redis", icon: SiRedis, color: "text-[#D82C20]" },
];

// eslint-disable-next-line no-unused-vars
const stats = [
  {
    number: "40+",
    label: "Happy Clients",
    sub: "Trusted by startups and enterprises",
    icon: <BsPeople className="text-emerald-600" size={22} />,
  },
  {
    number: "50+",
    label: "Projects Delivered",
    sub: "Successful digital products shipped globally",
    icon: <FaRocket className="text-emerald-600" size={22} />,
  },
  {
    number: "Reliable",
    label: "Uptime Focus",
    sub: "Reliable systems that scale",
    icon: <BsShieldCheck className="text-emerald-600" size={22} />,
  },
  {
    number: "10+",
    label: "Tech Experts",
    sub: "Engineers, architects & problem solvers",
    icon: <FaCode className="text-emerald-600" size={22} />,
  },
  {
    number: "Global",
    label: "Worldwide",
    sub: "Delivering solutions across the globe",
    icon: <FaGlobe className="text-emerald-600" size={22} />,
  },
];

// eslint-disable-next-line no-unused-vars
const processSteps = [
  {
    num: "01",
    title: "Discover",
    desc: "Deep-dive into your business goals, user needs, and technical landscape.",
  },
  {
    num: "02",
    title: "Design",
    desc: "Craft pixel-perfect UI/UX and scalable system architectures.",
  },
  {
    num: "03",
    title: "Develop",
    desc: "Build robust, full-stack solutions with agile engineering sprints.",
  },
  {
    num: "04",
    title: "Deliver",
    desc: "Deploy with near-zero downtime and scale globally on cloud infrastructure.",
  },
];

export default function AboutPage() {
  pageTitle("About");
  return (
    <main className="relative overflow-hidden bg-[#fbfdfd]">
      {/* ============ BRAND TAGLINE: Algorithm + Pristine — Logo + Service Page matched ============ */}
      <section className="relative bg-gradient-to-b from-white to-gray-50/60 px-4 pb-8 pt-[130px] sm:px-6 sm:pb-10 sm:pt-[140px]">
        {/* Decorative dots — same as ServicePage hero: Algo teal left, Stine gold right */}
        <div
          className="pointer-events-none absolute left-[5%] top-[35%] h-36 w-52 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(#1B6B80 1.4px, transparent 1.4px)",
            backgroundSize: "12px 12px",
          }}
        />
        <div
          className="pointer-events-none absolute right-[5%] top-[40%] h-28 w-40 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(#E7AB27 1.4px, transparent 1.4px)",
            backgroundSize: "12px 12px",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[900px] text-center">
          {/* Label like ServicePage */}
          <div className="mb-4 inline-flex items-center gap-3">
            <div className="h-[2px] w-10 bg-gradient-to-r from-transparent to-[#1B6B80]" />
            <span className="text-[11px] font-bold uppercase tracking-[3px] text-[#1B6B80]">
              Our Philosophy
            </span>
            <div className="h-[2px] w-10 bg-gradient-to-l from-transparent to-[#1B6B80]" />
          </div>

          {/* Main tagline — simple text, no card */}
          <h2 className="text-[clamp(32px,4.8vw,52px)] font-bold leading-none tracking-[-0.03em]">
            <span style={{ color: "#1B6B80" }}>Algorithm</span>
            <span className="mx-2 font-light text-gray-400">+</span>
            <span style={{ color: "#E7AB27" }}>Pristine</span>
          </h2>

          <p className="mx-auto mt-4 max-w-[740px] text-[14px] leading-[1.8] text-gray-500 sm:text-[15px]">
            <span className="font-semibold text-[#000000]">Algorithm</span> —
            how we approach every problem: with logic, intelligence and
            precision.
            <br className="sm:hidden" />
            <br />
            <span style={{ color: "#000000" }} className="font-semibold">
              Pristine
            </span>{" "}
            — what we set out to deliver: quality, clarity and excellence.
          </p>
        </div>
      </section>

      {/* ============ HERO: Pixel-Perfect Match ============ */}
      <section className="relative mt-5 overflow-hidden bg-gradient-to-br from-[#f8fffe] via-white to-[#f0faf8] mt-3  max-[991px]:pt-[40px] max-[991px]:pb-[50px]">
        <section className="relative overflow-hidden bg-[#fbfdfc] py-20 lg:py-24">
          <div className="relative z-10 mx-auto max-w-[1240px] px-5 sm:px-7">
            <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
              <div className="relative z-20 max-w-[560px]">
                {/* Section label */}
                <div className="mb-7 flex items-center gap-3">
                  <span className="h-[2px] w-12 bg-[#1b667a]" />

                  <span className="text-[11px] font-bold uppercase tracking-[4px] text-[#1b667a]">
                    About Us
                  </span>

                  <span className="flex gap-1">
                    <i className="h-1 w-1 rounded-full bg-[#1b667a]/70" />
                    <i className="h-1 w-1 rounded-full bg-[#1b667a]/40" />
                    <i className="h-1 w-1 rounded-full bg-[#1b667a]/20" />
                  </span>
                </div>

                {/* Main heading */}
                <h1 className="max-w-[540px] text-[clamp(42px,5vw,56px)] font-semibold leading-[0.98] tracking-[-0.045em] text-[#101828]">
                  <span className="block">We Don't Just</span>

                  <span className="block">Write Code.</span>

                  <span className="mt-2 block bg-gradient-to-r from-[#075E68] via-[#0E9F7A] to-[#10B981] bg-clip-text text-transparent">
                    We Architect
                  </span>

                  <span className="block bg-gradient-to-r from-[#0E9F7A] to-[#18B9A5] bg-clip-text text-transparent">
                    Solutions.
                  </span>
                </h1>

                {/* Decorative line */}
                <div className="my-7 flex items-center gap-1.5">
                  <span className="h-[3px] w-8 rounded-full bg-[#1b667a]" />
                  <span className="h-[3px] w-5 rounded-full bg-[#1b667a]/50" />
                  <span className="h-[3px] w-3 rounded-full bg-[#1b667a]/25" />
                </div>

                {/* Description */}
                <p className="max-w-[500px] text-[15px] leading-[1.8] text-slate-500 lg:text-[16px]">
                  We believe the most powerful digital products are built when
                  business strategies are executed directly by elite technical
                  talent. When you partner with us, you work directly with the
                  core engineers writing your code.
                </p>

                {/* =====================================================
            VALUE POINTS
        ===================================================== */}
                <div className="mt-2 grid max-w-[530px] grid-cols-3 border-y border-slate-200/80 py-5">
                  {/* Strategy */}
                  <div className="group border-r border-slate-200/80 pr-4">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 transition-all duration-300 group-hover:bg-[#1b667a]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-[#1b667a] group-hover:text-white"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>

                    <h3 className="text-[12px] font-bold text-slate-900">
                      Strategy First
                    </h3>

                    <p className="mt-1 text-[10px] leading-4 text-slate-400">
                      Aligning technology with business goals.
                    </p>
                  </div>

                  {/* Scale */}
                  <div className="group border-r border-slate-200/80 px-4">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 transition-all duration-300 group-hover:bg-[#1b667a]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-[#1b667a] group-hover:text-white"
                      >
                        <path d="m8 9-3 3 3 3" />
                        <path d="m16 9 3 3-3 3" />
                        <path d="m14 5-4 14" />
                      </svg>
                    </div>

                    <h3 className="text-[12px] font-bold text-slate-900">
                      Engineered to Scale
                    </h3>

                    <p className="mt-1 text-[10px] leading-4 text-slate-400">
                      Robust systems built for long-term growth.
                    </p>
                  </div>

                  {/* Impact */}
                  <div className="group pl-4">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 transition-all duration-300 group-hover:bg-[#1b667a]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-[#1b667a] group-hover:text-white"
                      >
                        <path d="m12 2 2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5L12 2Z" />
                      </svg>
                    </div>

                    <h3 className="text-[12px] font-bold text-slate-900">
                      Impact Driven
                    </h3>

                    <p className="mt-1 text-[10px] leading-4 text-slate-400">
                      Creating measurable business outcomes.
                    </p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className=" flex items-center gap-3">
                  <a
                    href="/contact"
                    className="inline-flex rounded-[12px] items-center justify-center gap-2 whitespace-nowrap bg-[#1b667a] px-6 py-2.5 text-[15px] font-semibold text-white shadow-[0_4px_16px_rgba(27,102,122,0.24)] transition-all duration-300 hover:bg-[#155568] hover:shadow-[0_6px_20px_rgba(27,102,122,0.32)] hover:-translate-y-0.5"
                  >
                    Get In Touch <FiArrowUpRight size={16} />
                  </a>
                  <a
                    href="/service"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-gray-300 bg-transparent px-6 py-2.5 text-[15px] font-semibold text-gray-700 transition-all duration-300 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Our Services <FiArrowRight size={16} />
                  </a>
                </div>
              </div>

              {/* =====================================================
          RIGHT VISUAL
      ===================================================== */}
              <div className="relative min-h-[520px] lg:min-h-[580px]">
                {/* Decorative green shape */}
                <div className="absolute -right-4 top-5 h-[88%] w-[92%] rounded-[70px] bg-gradient-to-br from-emerald-100 via-teal-50 to-transparent opacity-80 blur-[1px]" />

                {/* Orbit lines */}
                <div className="absolute -left-12 top-[28%] h-[280px] w-[280px] rounded-full border border-[#1b667a]/10" />

                <div className="absolute -left-20 top-[22%] h-[360px] w-[360px] rounded-full border border-[#1b667a]/[0.07]" />

                {/* Main image */}
                <div
                  className="absolute right-0 top-0 h-[470px] w-[92%] overflow-hidden rounded-[48px] shadow-[0_30px_80px_rgba(15,23,42,0.13)] lg:h-[520px]"
                  style={{
                    clipPath:
                      "polygon(12% 0%, 100% 0%, 100% 82%, 90% 100%, 0% 100%, 0% 16%)",
                  }}
                >
                  <img
                    src="/images/digital-agency/about_1.jpeg"
                    alt="AlgoStine team at work"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.025]"
                  />

                  {/* image overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#073B40]/10 via-transparent to-[#1b667a]/20" />

                  {/* process bar */}
                  <div className="absolute right-5 top-6 flex items-center gap-1.5 rounded-2xl border border-white/20 bg-black/20 px-3 py-2 backdrop-blur-md">
                    {["01", "02", "03", "04", "05"].map((item, index) => (
                      <React.Fragment key={item}>
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-lg text-[9px] font-bold ${
                            index === 2
                              ? "bg-[#1b667a] text-white"
                              : "bg-white/80 text-slate-700"
                          }`}
                        >
                          {item}
                        </div>

                        {index < 4 && (
                          <span className="text-[9px] text-white/60">→</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* process label */}
                  <div className="absolute bottom-8 left-8">
                    <p className="text-[10px] font-semibold uppercase tracking-[3px] text-white/70">
                      Our Engineering Process
                    </p>

                    <p className="mt-2 text-xl font-bold text-white">
                      Discover → Build → Deliver
                    </p>
                  </div>
                </div>

                {/* =====================================================
            QUOTE CARD
        ===================================================== */}
                <div className="absolute -bottom-4 right-0 z-30 w-[235px] rounded-[26px] bg-[#101B2D] p-5 shadow-[0_25px_60px_rgba(15,23,42,0.22)] lg:right-[-10px]">
                  <div className="mb-3 text-3xl leading-none text-emerald-400">
                    “
                  </div>

                  <p className="text-[13px] font-medium leading-[1.55] text-white">
                    Great software starts with understanding problems{" "}
                    <span className="font-bold text-emerald-400">deeply.</span>
                  </p>

                  <div className="mt-4 flex items-center gap-2">
                    <span className="h-px w-7 bg-emerald-400/60" />

                    <span className="text-[9px] font-semibold text-white/45">
                      THE ALGOSTINE TEAM
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section className="relative overflow-hidden bg-[#fbfdfc] py-16 lg:py-20">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-32 top-20 h-[400px] w-[400px] rounded-full bg-emerald-100/30 blur-[120px]" />
          <div className="absolute -right-40 bottom-0 h-[400px] w-[400px] rounded-full bg-teal-100/20 blur-[120px]" />
          <div
            className="absolute left-0 top-[15%] h-32 w-32 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(#0E9F7A 1.5px, transparent 1.5px)",
              backgroundSize: "12px 12px",
              maskImage: "radial-gradient(circle, black, transparent 70%)",
              WebkitMaskImage:
                "radial-gradient(circle, black, transparent 70%)",
            }}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-[1200px] px-4 sm:px-6">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            {/* LEFT — Image */}
            <div className="relative order-1">
              {/* Main image */}
              <div className="relative overflow-hidden rounded-[32px] shadow-[0_25px_70px_rgba(15,23,42,0.12)]">
                <img
                  src="/images/digital-agency/about_2.jpeg"
                  alt="AlgoStine team"
                  className="h-[320px] w-full object-cover transition-transform duration-700 hover:scale-[1.03] sm:h-[380px] lg:h-[440px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071D25]/40 via-transparent to-transparent" />

                {/* Top label */}
                <div className="absolute left-5 top-5 rounded-full border border-white/20 bg-black/20 px-3 py-1.5 backdrop-blur-md">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                    <span className="text-[9px] font-bold uppercase tracking-[2px] text-white">
                      Engineering Excellence
                    </span>
                  </div>
                </div>

                {/* Bottom text */}
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-[9px] font-bold uppercase tracking-[3px] text-emerald-300">
                    Our Engineering DNA
                  </p>
                  <p className="mt-1 max-w-[320px] text-[18px] font-bold leading-[1.15] text-white">
                    Building technology that creates a lasting business
                    advantage.
                  </p>
                </div>
              </div>

              {/* Floating stat - top right */}
              <div className="absolute -right-3 top-[-10px] z-30 w-[170px] rounded-[18px] bg-[#101827] p-3.5 shadow-[0_15px_40px_rgba(15,23,42,0.2)] sm:-right-5 sm:w-[190px] sm:p-4">
                <div className="flex gap-2.5">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#1b667a] text-white">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[18px] font-bold leading-none text-white">
                      40+
                    </p>
                    <p className="mt-0.5 text-[9px] font-bold text-emerald-400">
                      Happy Clients
                    </p>
                    <p className="mt-0.5 text-[8px] text-white/50">
                      Trusted by startups
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating stat - bottom right */}
              <div className="absolute -bottom-4 right-[-8px] z-30 w-[220px] rounded-[20px] bg-gradient-to-br from-[#08776F] to-[#0E9F7A] p-4 shadow-[0_20px_50px_rgba(14,159,122,0.25)] sm:-bottom-5 sm:right-[-15px] sm:w-[250px] sm:p-4.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <path d="m4 19 4-5 4 3 7-9" />
                      <path d="M20 8v6" />
                      <path d="M20 8h-6" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[22px] font-bold leading-none text-white">
                      50+
                    </p>
                    <p className="mt-0.5 text-[9px] font-bold text-emerald-100">
                      Projects Delivered
                    </p>
                    <p className="mt-0.5 text-[8px] text-white/60">
                      Shipped globally
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — Content + Cards */}
            <div className="relative order-2 ml-16">
              {/* Label */}
              <div className="mb-4 flex items-center gap-2.5">
                <span className="h-[2px] w-8 bg-[#1b667a]" />
                <span className="text-[11px] font-bold uppercase tracking-[3px] text-emerald-600">
                  Why Choose Us
                </span>
              </div>

              {/* Heading */}
              <h2 className="mb-4 text-[clamp(30px,3.8vw,48px)] font-bold leading-[1.05] tracking-[-0.03em] text-[#101828]">
                Built <span className="text-[#1b667a]">Different.</span>
                <br />
                Engineered <span className="text-[#1b667a]">Better.</span>
              </h2>

              {/* Description */}
              <p className="mb-6 max-w-[480px] text-[16px] leading-[1.75] text-slate-500">
                We combine cutting-edge technology with rock-solid engineering
                practices to build digital solutions that make a real impact.
              </p>

              {/* 2x2 Cards */}
              <div className="grid gap-3 sm:grid-cols-2">
                {values.map((v, i) => (
                  <div
                    key={i}
                    className="group relative overflow-hidden rounded-[16px] border border-slate-200/70 bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.03)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_36px_rgba(15,23,42,0.08)]"
                  >
                    <div
                      className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl transition-colors duration-300 ${
                        i === 0
                          ? "bg-emerald-50 text-emerald-600 group-hover:bg-[#1b667a] group-hover:text-white"
                          : i === 1
                            ? "bg-teal-50 text-teal-600 group-hover:bg-teal-500 group-hover:text-white"
                            : i === 2
                              ? "bg-sky-50 text-sky-600 group-hover:bg-sky-500 group-hover:text-white"
                              : "bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white"
                      }`}
                    >
                      {v.icon}
                    </div>
                    <h3 className="mb-1.5 text-[16px] font-bold text-slate-900">
                      {v.title}
                    </h3>
                    <p className="text-[14px] leading-[1.7] text-slate-500">
                      {v.desc}
                    </p>

                    {/* Bottom line */}
                    <div
                      className={`absolute bottom-0 left-0 h-[2px] w-0 transition-all duration-500 group-hover:w-full ${
                        i === 0
                          ? "bg-[#1b667a]"
                          : i === 1
                            ? "bg-teal-500"
                            : i === 2
                              ? "bg-sky-500"
                              : "bg-amber-500"
                      }`}
                    />
                  </div>
                ))}
              </div>

              {/* Trust line */}
              <div className="mt-5 flex items-center gap-2.5">
                <div className="flex">
                  <span className="h-6 w-6 rounded-full border-2 border-white bg-emerald-100" />
                  <span className="-ml-1.5 h-6 w-6 rounded-full border-2 border-white bg-teal-100" />
                  <span className="-ml-1.5 h-6 w-6 rounded-full border-2 border-white bg-emerald-200" />
                </div>
                <span className="text-[13px] font-medium text-slate-400">
                  Trusted engineering for ambitious businesses
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
    HOW WE WORK / OUR PROCESS
========================================================= */}
      <section className="relative overflow-hidden bg-[#fbfdfc] py-24 lg:py-32">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-40 h-[520px] w-[520px] rounded-full bg-emerald-100/40 blur-[150px]" />

          <div className="absolute -right-40 bottom-10 h-[500px] w-[500px] rounded-full bg-teal-100/30 blur-[150px]" />

          {/* Dots */}
          <div
            className="absolute right-[5%] top-[12%] h-36 w-52 opacity-25"
            style={{
              backgroundImage:
                "radial-gradient(#10b981 1.5px, transparent 1.5px)",
              backgroundSize: "12px 12px",
            }}
          />

          <div
            className="absolute bottom-8 left-[2%] h-36 w-48 opacity-25"
            style={{
              backgroundImage:
                "radial-gradient(#10b981 1.5px, transparent 1.5px)",
              backgroundSize: "11px 11px",
            }}
          />
        </div>

        {/* =======================================================
      MAIN
  ======================================================= */}
        <div className="relative z-10 mx-auto max-w-[1320px] px-6 xl:px-8">
          <div
            className="
        grid
        items-center
        gap-16
        lg:grid-cols-[0.92fr_1.08fr]
        lg:gap-24
        xl:gap-28
      "
          >
            {/* ===================================================
          LEFT SIDE
      =================================================== */}
            <div className="relative">
              {/* Label */}
              <div className="mb-6 flex items-center gap-3">
                <span className="h-[2px] w-10 bg-[#1b667a]" />

                <span className="text-[11px] font-bold uppercase tracking-[3.5px] text-emerald-600">
                  How We Work
                </span>

                <div className="flex gap-1">
                  <span className="h-1 w-1 rounded-full bg-[#1b667a]" />
                  <span className="h-1 w-1 rounded-full bg-emerald-400" />
                  <span className="h-1 w-1 rounded-full bg-emerald-300" />
                </div>
              </div>

              {/* Heading */}
              <h2
                className="
            max-w-[560px]
            text-[clamp(48px,5.2vw,65px)]
            font-bold
            leading-[0.94]
            tracking-[-0.055em]
            text-[#101828]
          "
              >
                Our{" "}
                <span className="bg-gradient-to-r from-[#075e68] via-[#087f78] to-[#10a879] bg-clip-text text-transparent">
                  Process.
                </span>
              </h2>

              {/* Accent */}
              <div className="mt-7 flex items-center gap-2">
                <span className="h-[3px] w-10 bg-[#1b667a]" />
                <span className="h-[3px] w-3 bg-emerald-300" />
                <span className="h-[3px] w-2 bg-emerald-200" />
              </div>

              {/* Description */}
              <p className="mt-7 max-w-[500px] text-[15px] leading-[1.85] text-slate-500">
                From initial discovery to final deployment, our streamlined
                process ensures every project is delivered with precision,
                scalability, and excellence.
              </p>

              {/* =================================================
            IMAGE WRAPPER
        ================================================= */}
              <div className="relative mt-12">
                {/* Rings */}
                <div
                  className="
              absolute
              -left-28
              -top-16
              h-[500px]
              w-[500px]
              rounded-full
              border
              border-emerald-300/20
            "
                />

                <div
                  className="
              absolute
              -left-14
              top-2
              h-[410px]
              w-[410px]
              rounded-full
              border
              border-emerald-300/15
            "
                />

                {/* Main Image */}
                <div
                  className="
              process-image
              relative
              z-10
              h-[460px]
              w-full
              overflow-hidden
              rounded-[38px]
              border
              border-white/80
              bg-slate-900
              shadow-[0_35px_90px_rgba(15,23,42,0.16)]
              lg:h-[530px]
              xl:h-[550px]
            "
                >
                  <img
                    src="/images/digital-agency/about_2.jpeg"
                    alt="AlgoStine engineering process"
                    className="
                h-full
                w-full
                object-cover
                transition-transform
                duration-700
                ease-out
                hover:scale-[1.04]
              "
                  />

                  {/* Dark gradient */}
                  <div
                    className="
                absolute
                inset-0
                bg-gradient-to-t
                from-[#06191d]/80
                via-[#06191d]/10
                to-transparent
              "
                  />

                  {/* Image label */}
                  <div
                    className="
                absolute
                left-6
                top-6
                rounded-full
                border
                border-white/20
                bg-black/20
                px-4
                py-2
                backdrop-blur-md
              "
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.9)]" />

                      <span className="text-[10px] font-bold uppercase tracking-[2px] text-white">
                        Engineering Excellence
                      </span>
                    </div>
                  </div>

                  {/* Image bottom content */}
                  <div className="absolute bottom-7 left-7 right-7">
                    <p className="text-[10px] font-bold uppercase tracking-[3px] text-emerald-300">
                      Built With Purpose
                    </p>

                    <p className="mt-2 max-w-[360px] text-[24px] font-bold leading-[1.15] tracking-tight text-white">
                      Strategy first.
                      <br />
                      Engineering always.
                    </p>
                  </div>
                </div>

                {/* =================================================
              FLOATING STAT
          ================================================= */}
                <div
                  className="
              process-stat
              absolute
              -bottom-8
              -right-4
              z-20
              w-[245px]
              rounded-[25px]
              border
              border-white/10
              bg-[#09272a]
              p-5
              shadow-[0_25px_65px_rgba(6,78,59,0.28)]
              lg:-right-8
            "
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="
                  flex
                  h-13
                  w-13
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[#1b667a]/15
                  text-emerald-400
                "
                    >
                      <svg
                        width="23"
                        height="23"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      >
                        <path d="M12 2v4" />
                        <path d="M12 18v4" />
                        <path d="M4.93 4.93l2.83 2.83" />
                        <path d="M16.24 16.24l2.83 2.83" />
                        <path d="M2 12h4" />
                        <path d="M18 12h4" />
                        <path d="M4.93 19.07l2.83-2.83" />
                        <path d="M16.24 7.76l2.83-2.83" />
                        <circle cx="12" cy="12" r="4" />
                      </svg>
                    </div>

                    <div>
                      <p className="text-[27px] font-bold leading-none text-white">
                        Reliable
                      </p>

                      <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400">
                        Engineering Focus
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 h-px bg-white/10" />

                  <p className="mt-3 text-[10px] leading-4 text-white/50">
                    Projects delivered with precision and engineering
                    excellence.
                  </p>
                </div>
              </div>
            </div>

            {/* ===================================================
          RIGHT SIDE — PROCESS CARDS
      =================================================== */}
            <div className="relative">
              {/* Timeline */}
              <div
                className="
            absolute
            left-[27px]
            top-[30px]
            bottom-[30px]
            hidden
            w-px
            bg-gradient-to-b
            from-emerald-200
            via-emerald-400
            to-emerald-200
            lg:block
          "
              />

              {/* =================================================
            CARD 01
        ================================================= */}
              <div className="process-item group relative mb-5 pl-0 lg:pl-[65px]">
                {/* Timeline point */}
                <div
                  className="
              absolute
              left-[18px]
              top-[38px]
              z-20
              hidden
              h-[19px]
              w-[19px]
              rounded-full
              border-[4px]
              border-[#fbfdfc]
              bg-[#1b667a]
              shadow-[0_0_0_5px_rgba(16,185,129,0.12)]
              lg:block
            "
                />

                <div
                  className="
              relative
              overflow-hidden
              rounded-[28px]
              border
              border-slate-200/80
              bg-white
              p-6
              shadow-[0_12px_40px_rgba(15,23,42,0.04)]
              transition-all
              duration-500
              hover:-translate-y-1
              hover:border-emerald-200
              hover:shadow-[0_25px_60px_rgba(15,23,42,0.09)]
            "
                >
                  {/* Accent background */}
                  <div
                    className="
                absolute
                -right-16
                -top-16
                h-40
                w-40
                rounded-full
                bg-emerald-50
                transition-transform
                duration-700
                group-hover:scale-150
              "
                  />

                  <div className="relative z-10 flex items-center gap-5">
                    {/* Number */}
                    <div
                      className="
                  flex
                  h-[78px]
                  w-[78px]
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-[22px]
                  border
                  border-emerald-100
                  bg-gradient-to-br
                  from-emerald-50
                  to-white
                  text-[25px]
                  font-bold
                  text-[#1b667a]
                "
                    >
                      01
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-[21px] font-bold tracking-[-0.02em] text-slate-900">
                          Discover
                        </h3>
                      </div>

                      <p className=" max-w-[520px] text-[15px] leading-6 text-slate-500">
                        Deep-dive into your business goals, user needs, and
                        technical landscape to define the right roadmap.
                      </p>
                    </div>
                  </div>

                  {/* Bottom progress */}
                  <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#1b667a] transition-all duration-500 group-hover:w-full" />
                </div>
              </div>

              {/* =================================================
            CARD 02
        ================================================= */}
              <div className="process-item group relative mb-5 pl-0 lg:pl-[65px]">
                <div
                  className="
              absolute
              left-[18px]
              top-[38px]
              z-20
              hidden
              h-[19px]
              w-[19px]
              rounded-full
              border-[4px]
              border-[#fbfdfc]
              bg-sky-500
              shadow-[0_0_0_5px_rgba(14,165,233,0.12)]
              lg:block
            "
                />

                <div
                  className="
              relative
              overflow-hidden
              rounded-[28px]
              border
              border-slate-200/80
              bg-white
              p-6
              shadow-[0_12px_40px_rgba(15,23,42,0.04)]
              transition-all
              duration-500
              hover:-translate-y-1
              hover:border-sky-200
              hover:shadow-[0_25px_60px_rgba(15,23,42,0.09)]
            "
                >
                  <div
                    className="
                absolute
                -right-16
                -top-16
                h-40
                w-40
                rounded-full
                bg-sky-50
                transition-transform
                duration-700
                group-hover:scale-150
              "
                  />

                  <div className="relative z-10 flex items-center gap-5">
                    <div
                      className="
                  flex
                  h-[78px]
                  w-[78px]
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-[22px]
                  border
                  border-sky-100
                  bg-gradient-to-br
                  from-sky-50
                  to-white
                  text-[25px]
                  font-bold
                  text-sky-500
                "
                    >
                      02
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="text-[21px] font-bold tracking-[-0.02em] text-slate-900">
                          Design
                        </h3>
                      </div>

                      <p className=" max-w-[520px] text-[15px] leading-6 text-slate-500">
                        Craft pixel-perfect UI/UX and scalable system
                        architectures that are intuitive and future-ready.
                      </p>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-sky-500 transition-all duration-500 group-hover:w-full" />
                </div>
              </div>

              {/* =================================================
            CARD 03
        ================================================= */}
              <div className="process-item group relative mb-5 pl-0 lg:pl-[65px]">
                <div
                  className="
              absolute
              left-[18px]
              top-[38px]
              z-20
              hidden
              h-[19px]
              w-[19px]
              rounded-full
              border-[4px]
              border-[#fbfdfc]
              bg-violet-500
              shadow-[0_0_0_5px_rgba(139,92,246,0.12)]
              lg:block
            "
                />

                <div
                  className="
              relative
              overflow-hidden
              rounded-[28px]
              border
              border-slate-200/80
              bg-white
              p-6
              shadow-[0_12px_40px_rgba(15,23,42,0.04)]
              transition-all
              duration-500
              hover:-translate-y-1
              hover:border-violet-200
              hover:shadow-[0_25px_60px_rgba(15,23,42,0.09)]
            "
                >
                  <div
                    className="
                absolute
                -right-16
                -top-16
                h-40
                w-40
                rounded-full
                bg-violet-50
                transition-transform
                duration-700
                group-hover:scale-150
              "
                  />

                  <div className="relative z-10 flex items-center gap-5">
                    <div
                      className="
                  flex
                  h-[78px]
                  w-[78px]
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-[22px]
                  border
                  border-violet-100
                  bg-gradient-to-br
                  from-violet-50
                  to-white
                  text-[25px]
                  font-bold
                  text-violet-500
                "
                    >
                      03
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="text-[21px] font-bold tracking-[-0.02em] text-slate-900">
                          Develop
                        </h3>
                      </div>

                      <p className="max-w-[520px] text-[15px] leading-6 text-slate-500">
                        Build robust, full-stack solutions with clean code,
                        modern technology, and agile engineering sprints.
                      </p>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-violet-500 transition-all duration-500 group-hover:w-full" />
                </div>
              </div>

              {/* =================================================
            CARD 04
        ================================================= */}
              <div className="process-item group relative pl-0 lg:pl-[65px]">
                <div
                  className="
              absolute
              left-[18px]
              top-[38px]
              z-20
              hidden
              h-[19px]
              w-[19px]
              rounded-full
              border-[4px]
              border-[#fbfdfc]
              bg-amber-500
              shadow-[0_0_0_5px_rgba(245,158,11,0.12)]
              lg:block
            "
                />

                <div
                  className="
              relative
              overflow-hidden
              rounded-[28px]
              border
              border-slate-200/80
              bg-white
              p-6
              shadow-[0_12px_40px_rgba(15,23,42,0.04)]
              transition-all
              duration-500
              hover:-translate-y-1
              hover:border-amber-200
              hover:shadow-[0_25px_60px_rgba(15,23,42,0.09)]
            "
                >
                  <div
                    className="
                absolute
                -right-16
                -top-16
                h-40
                w-40
                rounded-full
                bg-amber-50
                transition-transform
                duration-700
                group-hover:scale-150
              "
                  />

                  <div className="relative z-10 flex items-center gap-5">
                    <div
                      className="
                  flex
                  h-[78px]
                  w-[78px]
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-[22px]
                  border
                  border-amber-100
                  bg-gradient-to-br
                  from-amber-50
                  to-white
                  text-[25px]
                  font-bold
                  text-amber-500
                "
                    >
                      04
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="text-[21px] font-bold tracking-[-0.02em] text-slate-900">
                          Deliver
                        </h3>
                      </div>

                      <p className="max-w-[520px] text-[15px] leading-6 text-slate-500">
                        Deploy with near-zero downtime and scale globally on
                        cloud infrastructure for maximum performance.
                      </p>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-amber-500 transition-all duration-500 group-hover:w-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ EXPERTISE SECTION ============ */}
      <section className="relative z-10 mt-20 mb-10 px-4 sm:px-6 pb-20 max-[991px]:pb-14 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute left-0 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-50/60 blur-[120px]" />
        <div className="absolute right-0 bottom-0 h-[400px] w-[400px] translate-x-1/4 translate-y-1/4 rounded-full bg-teal-50/60 blur-[100px]" />

        <div className="relative mx-auto max-w-[1200px]">
          {/* Section Header */}
          <div className="mb-14 text-center">
            <div className="mb-4 inline-flex items-center gap-2.5">
              <div className="h-[2px] w-8 bg-emerald-400" />
              <span className="text-[11px] font-bold uppercase tracking-[3px] text-emerald-600">
                Our Expertise
              </span>
              <div className="h-[2px] w-8 bg-emerald-400" />
            </div>
            <h2 className="mb-3 text-[clamp(26px,3vw,40px)] font-bold leading-[1.15] text-gray-900">
              Architecting Scalable Software & AI Solutions
            </h2>
            <p className="mx-auto max-w-[780px] text-[18px] leading-[1.8] text-gray-500">
              We deliver end-to-end digital engineering by bridging the gap
              between complex enterprise requirements and high-performance code.
              From building secure AWS cloud infrastructure to integrating
              predictive LLMs and machine learning algorithms, our developer-led
              team pushes the boundaries of modern technology.
            </p>
          </div>

          {/* Expertise Cards Grid */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: "🧠",
                title: "AI & Machine Learning",
                desc: "Custom ML models, LLM integration, computer vision, and intelligent automation solutions.",
                color: "from-violet-500 to-purple-600",
                bg: "bg-violet-50",
                border: "border-violet-100",
                hoverBorder: "hover:border-violet-300",
                tag: "Deep Learning",
              },
              {
                icon: "☁️",
                title: "Cloud Architecture",
                desc: "AWS, GCP, Azure infrastructure design with auto-scaling, CI/CD pipelines, and high availability.",
                color: "from-sky-500 to-blue-600",
                bg: "bg-sky-50",
                border: "border-sky-100",
                hoverBorder: "hover:border-sky-300",
                tag: "DevOps",
              },
              {
                icon: "⚛️",
                title: "Full-Stack Development",
                desc: "React, Node.js, TypeScript, Python — building performant apps from prototype to production.",
                color: "from-[#1b667a] to-teal-600",
                bg: "bg-emerald-50",
                border: "border-emerald-100",
                hoverBorder: "hover:border-emerald-300",
                tag: "MERN Stack",
              },
              {
                icon: "🎨",
                title: "UI/UX Design",
                desc: "Human-centered design systems, interactive prototypes, and pixel-perfect interfaces.",
                color: "from-rose-500 to-pink-600",
                bg: "bg-rose-50",
                border: "border-rose-100",
                hoverBorder: "hover:border-rose-300",
                tag: "Figma",
              },
              {
                icon: "🔒",
                title: "Security & Compliance",
                desc: "SOC 2, GDPR, HIPAA compliance with end-to-end encryption and threat monitoring.",
                color: "from-amber-500 to-orange-600",
                bg: "bg-amber-50",
                border: "border-amber-100",
                hoverBorder: "hover:border-amber-300",
                tag: "Zero Trust",
              },
              {
                icon: "📊",
                title: "Data Engineering",
                desc: "ETL pipelines, real-time analytics, data warehousing, and business intelligence dashboards.",
                color: "from-cyan-500 to-teal-600",
                bg: "bg-cyan-50",
                border: "border-cyan-100",
                hoverBorder: "hover:border-cyan-300",
                tag: "Big Data",
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`group relative overflow-hidden rounded-[15px] border ${item.border} ${item.hoverBorder} bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)]`}
              >
                {/* Gradient corner accent */}
                <div
                  className={`absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br ${item.color} opacity-10 transition-all duration-500 group-hover:scale-[2.5] group-hover:opacity-20`}
                />

                {/* Icon */}
                <div
                  className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${item.bg} text-2xl shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}
                >
                  {item.icon}
                </div>

                {/* Content */}
                <h3 className="mb-2.5 text-[20px] font-bold tracking-[-0.01em] text-gray-900">
                  {item.title}
                </h3>
                <p className="mb-4 text-[15px] leading-[1.7] text-gray-500">
                  {item.desc}
                </p>

                {/* Tag */}
                <span
                  className={`inline-block rounded-full ${item.bg} px-3.5 py-1 text-[12px] font-semibold uppercase tracking-wider text-gray-600`}
                >
                  {item.tag}
                </span>

                {/* Bottom gradient line */}
                <div
                  className={`absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r ${item.color} transition-all duration-500 group-hover:w-full`}
                />
              </div>
            ))}
          </div>

          {/* Tech Stack Marquee */}
          <div
            className="mt-14 overflow-hidden rounded-[20px] border border-gray-100 bg-white/80 py-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] backdrop-blur-sm"
            aria-label="Technology stack"
          >
            <div className="flex w-max animate-[techMarquee_30s_linear_infinite] items-center gap-8 px-6 hover:[animation-play-state:paused] sm:gap-10">
              {[...techStack, ...techStack].map((tech, i) => {
                const TechIcon = tech.icon;
                return (
                  <div
                    key={`${tech.name}-${i}`}
                    className="flex shrink-0 items-center gap-2.5 text-gray-500 transition-colors duration-300 hover:text-gray-900"
                  >
                    <TechIcon
                      className={`text-[22px] ${tech.color}`}
                      aria-hidden="true"
                    />
                    <span className="whitespace-nowrap text-[14px] font-semibold">
                      {tech.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* CTA */}
          <div className="mt-10 text-center">
            <a
              href="/service"
              className="group inline-flex items-center gap-2.5 bg-gradient-to-r from-[#1b667a] to-teal-600 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-600/25 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-600/30 hover:scale-[1.03] rounded-[15px]"
            >
              Explore Our Services
              <FiArrowRight
                className="transition-transform duration-300 group-hover:translate-x-1"
                size={16}
              />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
